package app.footyguide.web.components

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*

/**
 * Stitch Paper & Ink Skeleton Loader.
 * High-performance, lightweight shimmer placeholders matching the editorial 600px grid.
 */
@Composable
fun SkeletonBox(
    width: CSSNumericValue<*>,
    height: CSSNumericValue<*>,
    borderRadius: CSSNumericValue<*> = 8.px,
    modifier: (StyleScope.() -> Unit)? = null
) {
    Div({
        classes("fg-shimmer-active")
        style {
            width(width)
            height(height)
            borderRadius(borderRadius)
            modifier?.invoke(this)
        }
    })
}

@Composable
fun SkeletonHeroBanner() {
    Div({
        style {
            margin(12.px, 16.px, 20.px)
            backgroundColor(Color("#FFFFFF"))
            border(1.px, LineStyle.Solid, Color("#E7E7E1"))
            borderRadius(16.px)
            padding(16.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
            property("box-shadow", "0 2px 8px rgba(22, 24, 29, 0.04)")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            SkeletonBox(width = 100.px, height = 16.px, borderRadius = 4.px)
            SkeletonBox(width = 60.px, height = 16.px, borderRadius = 4.px)
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                padding(8.px, 0.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                }
            }) {
                SkeletonBox(width = 44.px, height = 44.px, borderRadius = 22.px)
                SkeletonBox(width = 80.px, height = 20.px, borderRadius = 4.px)
            }

            SkeletonBox(width = 24.px, height = 20.px, borderRadius = 4.px)

            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                }
            }) {
                SkeletonBox(width = 80.px, height = 20.px, borderRadius = 4.px)
                SkeletonBox(width = 44.px, height = 44.px, borderRadius = 22.px)
            }
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
                paddingTop(8.px)
                property("border-top", "1px solid #F4F4F5")
            }
        }) {
            SkeletonBox(width = 90.px, height = 14.px, borderRadius = 4.px)
            SkeletonBox(width = 70.px, height = 22.px, borderRadius = 6.px)
        }
    }
}

@Composable
fun SkeletonMatchCard() {
    Div({
        style {
            backgroundColor(Color("#FFFFFF"))
            border(1.px, LineStyle.Solid, Color("#E7E7E1"))
            borderRadius(14.px)
            padding(14.px, 16.px)
            margin(0.px, 16.px, 10.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.SpaceBetween)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
                flex(1)
            }
        }) {
            SkeletonBox(width = 36.px, height = 18.px, borderRadius = 4.px)
            SkeletonBox(width = 28.px, height = 28.px, borderRadius = 14.px)
            SkeletonBox(width = 110.px, height = 16.px, borderRadius = 4.px)
            SkeletonBox(width = 16.px, height = 14.px, borderRadius = 4.px)
            SkeletonBox(width = 110.px, height = 16.px, borderRadius = 4.px)
            SkeletonBox(width = 28.px, height = 28.px, borderRadius = 14.px)
        }

        SkeletonBox(width = 54.px, height = 20.px, borderRadius = 6.px)
    }
}

@Composable
fun SkeletonGamesList(count: Int = 3) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            paddingTop(6.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                padding(12.px, 16.px, 8.px)
            }
        }) {
            SkeletonBox(width = 120.px, height = 18.px, borderRadius = 4.px)
            SkeletonBox(width = 50.px, height = 14.px, borderRadius = 4.px)
        }

        repeat(count) {
            SkeletonMatchCard()
        }
    }
}
